import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts'
import { useTranslation } from 'react-i18next'

/**
 * Alder-profil chart shared by Klasse-detalj, Selger-detalj and Kjøper-detalj
 * (#482). Draws the "Alle" total line plus one line per selected merke.
 *
 * Lines plot **share of that series' own total** per age bucket, not raw
 * counts — otherwise a high-volume brand dwarfs every other line and the
 * shapes cannot be compared, which is the whole point of the overlay (AC2).
 */

export interface AlderSeriesBucket {
  aar: number
  antall: number
  andel_pct: number
}

export interface AlderSeries {
  merke: string
  buckets: AlderSeriesBucket[]
  over_25: number
  over_25_andel_pct: number
  total: number
}

/** Buyer-age series (#485) carry the same shape under different field names. */
export interface KjoperAlderSeries {
  merke: string
  buckets: { alder: number; antall: number; andel_pct: number }[]
  over_75: number
  over_75_andel_pct: number
  total: number
}

/** Re-key a buyer-age series onto the chart's vehicle-age field names. */
export const fromKjoperAlderSeries = (series: KjoperAlderSeries[]): AlderSeries[] =>
  series.map(s => ({
    merke: s.merke,
    buckets: s.buckets.map(b => ({ aar: b.alder, antall: b.antall, andel_pct: b.andel_pct })),
    over_25: s.over_75,
    over_25_andel_pct: s.over_75_andel_pct,
    total: s.total,
  }))

// "Alle" keeps the page's original cyan; brands cycle the shared palette.
const TOTAL_COLOR = '#06b6d4'
const BRAND_COLORS = [
  '#e11d48', // rose-600
  '#7c3aed', // violet-600
  '#059669', // emerald-600
  '#d97706', // amber-600
  '#2563eb', // blue-600
  '#db2777', // pink-600
  '#0891b2', // cyan-700
  '#65a30d', // lime-600
]

export const colorFor = (merke: string, i: number): string =>
  merke === 'Alle' ? TOTAL_COLOR : BRAND_COLORS[(i - 1 + BRAND_COLORS.length) % BRAND_COLORS.length]

/** Recharts wants one row per x value with a key per series. */
export function toChartRows(series: AlderSeries[]): Record<string, number>[] {
  const years = series[0]?.buckets.map(b => b.aar) ?? []
  return years.map((aar, idx) => {
    const row: Record<string, number> = { aar }
    for (const s of series) row[s.merke] = s.buckets[idx]?.andel_pct ?? 0
    return row
  })
}

interface Props {
  series: AlderSeries[]
  xLabel?: string
}

export default function AldersprofilChart({ series, xLabel }: Props) {
  const { t } = useTranslation('bruktbil')
  if (!series.length) return null
  const rows = toChartRows(series)

  return (
    <div className="card p-4">
      <ResponsiveContainer width="100%" height={300}>
        <LineChart data={rows} margin={{ top: 8, right: 16, left: 0, bottom: 16 }}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} />
          <XAxis
            dataKey="aar"
            tick={{ fontSize: 11 }}
            label={{ value: xLabel ?? t('alderChart.xLabel'), position: 'insideBottom', offset: -10, fontSize: 11 }}
          />
          <YAxis
            tick={{ fontSize: 11 }}
            tickFormatter={(v: number) => `${v}%`}
            label={{ value: t('alderChart.yLabel'), angle: -90, position: 'insideLeft', fontSize: 11 }}
          />
          <Tooltip
            formatter={(v: number, name: string) => [`${v.toLocaleString('nb-NO')} %`, name]}
            labelFormatter={l => t('units.aar', { value: l })}
          />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          {series.map((s, i) => (
            <Line
              key={s.merke}
              type="monotone"
              dataKey={s.merke}
              name={`${s.merke === 'Alle' ? t('common.alle') : s.merke} (${s.total.toLocaleString('nb-NO')})`}
              stroke={colorFor(s.merke, i)}
              strokeWidth={s.merke === 'Alle' ? 2.5 : 2}
              dot={{ r: 2 }}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  )
}
